'use client';

import * as React from 'react';
import { fmtNum, fmtPct } from '@slims/shared/calculations';
import type { EnergyMember, Member, SharedEnergy } from '@slims/shared/types';

import { Button, Grid, Row, Stack, Text } from '../../atoms';
import { SlChart } from '../../charts';
import {
	BodySkeleton,
	CellTitleSub,
	ChartSkeleton,
	DataTable,
	type DataTableColumn,
	DetailField,
	DetailTextarea,
	Footnote,
	KpiBig,
	KpiSkeleton,
	LabeledBox,
	ListSkeleton,
	MilestoneItem,
	PanelCard,
	PanelCardSkeleton,
	PctBarCell,
	StatusBlock,
	StimaInfo,
	Tag,
	type TagTone,
} from '../../molecules';
import { PERIOD_CURRENT, periodMonth } from '../../lib/period-tabs';

const CO2_KG_PER_KWH = 0.233;

const TIPO_BADGE: Record<string, { label: string; tone: TagTone }> = {
	residential: { label: 'Famiglia', tone: 'green' },
	office: { label: 'Ufficio', tone: 'blue' },
	shop: { label: 'Negozio', tone: 'orange' },
	sme: { label: 'Impresa', tone: 'purple' },
	factory: { label: 'Fabbrica', tone: 'purple' },
	public_administration: { label: 'Ente pubblico', tone: 'blue' },
	ets: { label: 'Associazione', tone: 'orange' },
};

export type MySlimsMember = EnergyMember & Partial<Pick<Member, 'email'>>;

function memberLabel(m: MySlimsMember): string {
	// Senza consenso il nome non arriva (INB-357): si dice, invece di un codice.
	if (m.anonimo) return 'Socio anonimo';
	const nome = m.nome || m.email || 'Socio';
	return m.sonoIo ? `${nome} (tu)` : nome;
}

function startOfDay(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

/** Intervallo `[from, to)` coperto dalla scheda periodo selezionata. */
function periodRange(period: string, today: Date): { from: Date; to: Date } {
	if (period === 'oggi') {
		const from = startOfDay(today);
		return { from, to: addDays(from, 1) };
	}
	if (period === 'ieri') {
		const from = addDays(startOfDay(today), -1);
		return { from, to: addDays(from, 1) };
	}
	const { year, month } = periodMonth(period === PERIOD_CURRENT ? null : period);
	const from = new Date(year, month - 1, 1);
	const to = period === PERIOD_CURRENT ? addDays(startOfDay(today), 1) : new Date(year, month, 1);
	return { from, to };
}

function pointHour(p: SharedEnergy): Date {
	return p.hour instanceof Date ? p.hour : new Date(p.hour);
}

function sumRange(points: SharedEnergy[], from: Date, to: Date) {
	let immessa = 0;
	let prelevata = 0;
	let condivisa = 0;
	for (const p of points) {
		const h = pointHour(p);
		if (h >= from && h < to) {
			immessa += p.totalImmessa;
			prelevata += p.totalPrelevata;
			condivisa += p.energiaCondivisa;
		}
	}
	return { immessa, prelevata, condivisa };
}

/** Numero con i decimali all'italiana (virgola), come nel resto dell'app. */
function decIt(n: number, digits: number): string {
	return n.toFixed(digits).replace('.', ',');
}

export interface MySlimsRulesForm {
	regoleGoverno: string;
	regoleAdesione: string;
	obiettiviCER: string;
}

/**
 * I numeri del socio che guarda, per il mese in corso (INB-363): i suoi consumi
 * e la sua produzione dai suoi misuratori, la sua quota di energia condivisa.
 * Senza, la vista mostrava come suoi i numeri
 * dell'intera configurazione — a una famiglia senza impianto 276 kWh di
 * «produzione» e 494 € di «risparmio».
 */
export interface MySlimsMemberKpi {
	memberId: string;
	sharedMonthKwh: number;
	consumptionMonthKwh?: number;
}

export interface MySlimsPersonal {
	ruolo: string;
	produzioneKwh: number;
	consumoKwh: number;
	condivisaKwh: number;
	/**
	 * Quanta parte dei tuoi valori è stimata (INB-354). `condivisa` tiene conto
	 * anche della produzione della configurazione, da cui la quota dipende.
	 */
	stima?: { consumo: number; produzione: number; condivisa: number };
}

export interface MySlimsViewProps {
	/**
	 * Periodo selezionato: 'oggi' | 'ieri' | quello di `PERIOD_CURRENT`
	 * ('corrente') | 'YYYY-MM'. Il selettore vive nel wiring (`MySlimsPage`),
	 * non qui — titolo e tab periodo stanno sopra questa vista, non dentro.
	 */
	period: string;
	/**
	 * Nome della configurazione. Non consumato qui — era solo il sottotitolo
	 * "· {mese}" di "Classifica Community", tolto su richiesta esplicita —
	 * resta nel tipo pubblico per non toccare il wiring.
	 */
	cerNome: string;
	/** Serie oraria immessa/prelevata/condivisa: alimenta KPI, grafico e leaderboard. */
	sharedEnergy: SharedEnergy[];
	/** Soci (tutti gli stati); i ritirati vengono esclusi dai conteggi qui dentro. */
	members: MySlimsMember[];
	sociTarget: number;
	rules: MySlimsRulesForm;
	isAdmin: boolean;
	onSaveRules: (rules: MySlimsRulesForm) => void;
	/**
	 * Presente quando guarda un socio: i KPI diventano i suoi, e il
	 * grafico si dichiara della configurazione. Assente per chi amministra, che
	 * guarda la configurazione.
	 */
	personal?: MySlimsPersonal | null;
	/**
	 * I numeri veri di ogni socio nel mese (riepilogo della Dashboard): energia
	 * condivisa e consumo dai suoi contatori. La
	 * classifica li usa invece di stimarli dal ruolo (INB-356).
	 */
	memberKpis?: MySlimsMemberKpi[];
	/**
	 * Un solo flag per l'intera pagina: quasi ogni card legge da `sharedEnergy`,
	 * `members` o `rules` (tutti dallo stesso giro di fetch del wiring), quindi
	 * si sbloccano insieme invece che a scatti — stesso principio di Incentivi.
	 */
	loading?: boolean;
}

/** Pagina "MySlims": KPI personali, andamento energia, traguardi e community. */
export function MySlimsView({
	period,
	personal = null,
	sharedEnergy,
	members,
	sociTarget,
	rules,
	isAdmin,
	onSaveRules,
	memberKpis = [],
	loading = false,
}: MySlimsViewProps) {
	const today = React.useMemo(() => new Date(), []);
	const isDayPeriod = period === 'oggi' || period === 'ieri';
	const [editingRules, setEditingRules] = React.useState(false);
	const [rulesForm, setRulesForm] = React.useState(rules);

	const activeMembers = React.useMemo(
		() => members.filter((m) => m.stato !== 'withdrawn'),
		[members],
	);
	const activeMembersCount = activeMembers.length;
	const hasAnyData = sharedEnergy.length > 0;

	const { from, to } = React.useMemo(() => periodRange(period, today), [period, today]);
	const periodSums = React.useMemo(
		() => sumRange(sharedEnergy, from, to),
		[sharedEnergy, from, to],
	);
	const hasPeriodData = periodSums.immessa > 0 || periodSums.prelevata > 0;

	const autoconsumoPct =
		hasPeriodData && periodSums.immessa > 0
			? Math.round(Math.min(100, (periodSums.condivisa / periodSums.immessa) * 100))
			: null;
	const immissioneNetta = Math.max(0, Math.round(periodSums.immessa - periodSums.condivisa));
	const co2Periodo = (periodSums.condivisa * CO2_KG_PER_KWH) / 1000;

	// Trend/didascalie: medie sull'intero storico caricato, indipendenti dal
	// periodo selezionato — nel mock v11 solo il numero grande della card
	// cambia scheda per scheda, non queste righe di supporto.
	const totals = sharedEnergy.reduce(
		(acc, p) => ({
			immessa: acc.immessa + p.totalImmessa,
			prelevata: acc.prelevata + p.totalPrelevata,
			condivisa: acc.condivisa + p.energiaCondivisa,
		}),
		{ immessa: 0, prelevata: 0, condivisa: 0 },
	);
	const produzioneGiornalieraMedia = hasAnyData ? Math.round(totals.immessa / 365) : 0;
	const co2Totale = hasAnyData ? (totals.condivisa * CO2_KG_PER_KWH) / 1000 : 0;

	const periodNoun = period === 'oggi' ? 'di oggi' : period === 'ieri' ? 'di ieri' : 'del mese';
	// Le card personali seguono il periodo scelto (N-19): l'etichetta lo dice.
	const personalPeriod =
		period === 'oggi'
			? 'oggi'
			: period === 'ieri'
				? 'ieri'
				: /^\d{4}-\d{2}$/.test(period)
					? new Date(
							Number(period.slice(0, 4)),
							Number(period.slice(5, 7)) - 1,
							1,
						).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
					: 'mese in corso';

	// Andamento energia: orario nella giornata per le schede Oggi/Ieri,
	// giornaliero nel mese per le altre — stessa logica di drawMyChart() nel
	// mock v11 (SLChart con 2 serie, non 3: qui non compare l'energia
	// condivisa, solo produzione vs prelievo).
	const energyChart = React.useMemo(() => {
		if (isDayPeriod) {
			const byHour = new Map<number, { prod: number; prel: number }>();
			for (const p of sharedEnergy) {
				const h = pointHour(p);
				if (h < from || h >= to) continue;
				const hour = h.getHours();
				const bucket = byHour.get(hour) ?? { prod: 0, prel: 0 };
				bucket.prod += p.totalImmessa;
				bucket.prel += p.totalPrelevata;
				byHour.set(hour, bucket);
			}
			const hours = [...byHour.keys()].sort((a, b) => a - b);
			return {
				labels: hours.map((h) => (h % 3 === 0 ? `${String(h).padStart(2, '0')}:00` : '')),
				hoverLabels: hours.map((h) => `${String(h).padStart(2, '0')}:00`),
				prod: hours.map((h) => byHour.get(h)?.prod ?? 0),
				prel: hours.map((h) => byHour.get(h)?.prel ?? 0),
			};
		}
		const byDay = new Map<number, { prod: number; prel: number }>();
		for (const p of sharedEnergy) {
			const h = pointHour(p);
			if (h < from || h >= to) continue;
			const day = h.getDate();
			const bucket = byDay.get(day) ?? { prod: 0, prel: 0 };
			bucket.prod += p.totalImmessa;
			bucket.prel += p.totalPrelevata;
			byDay.set(day, bucket);
		}
		const days = [...byDay.keys()].sort((a, b) => a - b);
		return {
			labels: days.map((d, i) => (i % 5 === 0 ? String(d) : '')),
			hoverLabels: days.map((d) => `Giorno ${d}`),
			prod: days.map((d) => byDay.get(d)?.prod ?? 0),
			prel: days.map((d) => byDay.get(d)?.prel ?? 0),
		};
	}, [sharedEnergy, from, to, isDayPeriod]);

	// Vista del socio (INB-363): i suoi numeri del periodo.
	const produce = personal?.ruolo === 'producer' || personal?.ruolo === 'prosumer';
	const coperturaPersonalePct =
		personal && personal.consumoKwh > 0
			? Math.round(Math.min(100, (personal.condivisaKwh / personal.consumoKwh) * 100))
			: null;
	const hasPersonalData = !!personal && (personal.consumoKwh > 0 || personal.produzioneKwh > 0);

	const totalSharedKwh = hasAnyData ? totals.condivisa : 0;
	const milestone100Mwh = hasAnyData
		? Math.min(100, Math.round((totalSharedKwh / 100_000) * 100))
		: 0;
	const milestoneSoci =
		sociTarget > 0 ? Math.min(100, Math.round((activeMembersCount / sociTarget) * 100)) : 0;

	// Classifica sui numeri veri del mese (INB-356). Prima i kWh di ogni socio
	// erano la media della configurazione pesata sul ruolo, e l'autoconsumo la
	// media ±5/0/−8 punti secondo la posizione: numeri che sembravano misurati.
	const kpiById = new Map(memberKpis.map((k) => [k.memberId, k]));
	const rankedMembers = [...activeMembers]
		.sort(
			(a, b) =>
				(kpiById.get(b.id)?.sharedMonthKwh ?? -1) - (kpiById.get(a.id)?.sharedMonthKwh ?? -1),
		)
		.slice(0, 6);

	const leaderboardColumns: DataTableColumn<MySlimsMember>[] = [
		{
			key: 'socio',
			header: 'Socio',
			render: (m) => <CellTitleSub title={memberLabel(m)} sub={m.email || '-'} />,
		},
		{
			key: 'tipo',
			header: 'Tipo',
			render: (m) => {
				const tipoBadge = TIPO_BADGE[m.tipo] ?? { label: m.tipo, tone: 'blue' as TagTone };
				return <Tag tone={tipoBadge.tone}>{tipoBadge.label}</Tag>;
			},
		},
		{
			key: 'kwh',
			header: 'kWh Cond.',
			variant: 'monoAccent',
			render: (m) => {
				const kpi = kpiById.get(m.id);
				return kpi ? `${fmtNum(Math.round(kpi.sharedMonthKwh))} kWh` : '-';
			},
		},
		{
			key: 'ac',
			// La quota del consumo del socio coperta da energia condivisa: un dato
			// del socio, da non confondere con l'autoconsumo della configurazione.
			header: 'Copertura consumi',
			render: (m) => {
				const kpi = kpiById.get(m.id);
				const consumo = kpi?.consumptionMonthKwh ?? 0;
				const pct =
					kpi && consumo > 0
						? Math.round(Math.min(100, (kpi.sharedMonthKwh / consumo) * 100))
						: null;
				return pct !== null ? <PctBarCell pct={pct} good={pct >= 70} /> : '-';
			},
		},
		{
			key: 'co2',
			header: 'CO₂',
			render: (m) => {
				const kpi = kpiById.get(m.id);
				return (
					<Text as="span" variant="monoInline" tone="success">
						{kpi ? `+${fmtNum(Math.round(kpi.sharedMonthKwh * CO2_KG_PER_KWH))} kg` : '-'}
					</Text>
				);
			},
		},
	];

	function saveRules() {
		onSaveRules(rulesForm);
		setEditingRules(false);
	}

	const ruleFields: [keyof MySlimsRulesForm, string, string][] = [
		['regoleGoverno', 'Governance', "Regole di partecipazione e voto dell'assemblea..."],
		['regoleAdesione', 'Adesione e Recesso', 'Condizioni per entrare e uscire dalla CER...'],
		['obiettiviCER', 'Obiettivi CER', 'Obiettivi energetici, ambientali e sociali...'],
	];

	if (loading) {
		return (
			<div>
				<Stack mb="lg">
					{/* Lo scheletro ha le card che arriveranno: a un socio le sue, non
					    quelle della configurazione, che vedeva per un attimo (N-17). */}
					<Grid columns="responsive4" gap="lg">
						{(isAdmin
							? ['Produzione', 'Autoconsumo', 'Immissione']
							: ['Il tuo consumo', 'La tua produzione', 'La tua energia condivisa']
						).map((title) => (
							<PanelCardSkeleton key={title} title={title}>
								<KpiSkeleton />
							</PanelCardSkeleton>
						))}
						<PanelCardSkeleton title="CO₂ Risparmiata">
							<KpiSkeleton />
						</PanelCardSkeleton>
					</Grid>
				</Stack>

				<Stack mb="lg">
					<PanelCardSkeleton
						title={isDayPeriod ? 'Andamento Giornaliero Energia' : 'Andamento Mensile Energia'}
					>
						<ChartSkeleton height={320} />
					</PanelCardSkeleton>
				</Stack>

				<Stack mb="lg">
					<PanelCardSkeleton title="Traguardi">
						<ListSkeleton rows={4} trailing />
					</PanelCardSkeleton>
				</Stack>

				<Stack mb="lg">
					<PanelCardSkeleton title="Classifica Community - Top Soci">
						<ListSkeleton rows={6} />
					</PanelCardSkeleton>
				</Stack>

				<PanelCardSkeleton title="Amministrazione">
					<Grid columns={2} gap="lg">
						<Stack gap="sm">
							<BodySkeleton className="h-2.5 w-32" />
							<ListSkeleton rows={2} />
						</Stack>
						<Stack gap="sm">
							<BodySkeleton className="h-2.5 w-40" />
							<Grid columns={2} gap="md">
								{ruleFields.map(([key]) => (
									<div key={key} className="rounded-lg border border-border/60 bg-background p-3">
										<BodySkeleton className="mb-2 h-2 w-20" />
										<BodySkeleton className="h-3 w-full" />
									</div>
								))}
							</Grid>
						</Stack>
					</Grid>
				</PanelCardSkeleton>
			</div>
		);
	}

	return (
		<div>
			<Stack mb="lg">
				{personal ? (
					<Grid columns="responsive4" gap="lg">
						<PanelCard title="Il tuo consumo" aside={<StimaInfo share={personal.stima?.consumo} />}>
							<KpiBig
								value={hasPersonalData ? `${fmtNum(Math.round(personal.consumoKwh))} kWh` : '-'}
								tone="accent"
								unit={`tuo · ${personalPeriod}`}
								trend={hasPersonalData ? 'dai tuoi contatori' : 'in attesa dati reali'}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard
							title="La tua produzione"
							aside={produce ? <StimaInfo share={personal.stima?.produzione} /> : undefined}
						>
							<KpiBig
								value={
									produce && hasPersonalData
										? `${fmtNum(Math.round(personal.produzioneKwh))} kWh`
										: '-'
								}
								tone="success"
								unit={produce ? `tua · ${personalPeriod}` : 'nessun impianto'}
								trend={produce ? 'dal tuo impianto' : 'non produci energia'}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard
							title="La tua energia condivisa"
							aside={<StimaInfo share={personal.stima?.condivisa} />}
						>
							<KpiBig
								value={hasPersonalData ? `${fmtNum(Math.round(personal.condivisaKwh))} kWh` : '-'}
								tone="accent"
								unit={`tua quota · ${personalPeriod}`}
								trend={
									coperturaPersonalePct !== null
										? `${coperturaPersonalePct}% del tuo consumo`
										: 'in attesa dati reali'
								}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard title="CO₂ Risparmiata">
							<KpiBig
								value={
									hasPersonalData
										? `${((personal.condivisaKwh * CO2_KG_PER_KWH) / 1000).toFixed(2)} t`
										: '-'
								}
								tone="purple"
								unit={`tua quota · ${personalPeriod}`}
								trend="sulla tua energia condivisa"
								trendTone="up"
							/>
						</PanelCard>
					</Grid>
				) : (
					<Grid columns="responsive4" gap="lg">
						<PanelCard title="Produzione">
							<KpiBig
								value={hasPeriodData ? `${fmtNum(Math.round(periodSums.immessa))} kWh` : '-'}
								tone="success"
								unit={`produzione ${periodNoun}`}
								trend={
									produzioneGiornalieraMedia > 0
										? `≈ ${fmtNum(produzioneGiornalieraMedia)} kWh/giorno`
										: 'in attesa dati reali'
								}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard title="Autoconsumo">
							<KpiBig
								value={autoconsumoPct !== null ? fmtPct(autoconsumoPct) : '-'}
								tone="accent"
								unit="coperto da condivisa"
								trend={
									autoconsumoPct !== null
										? autoconsumoPct >= 70
											? '✓ sopra media configurazione'
											: '↑ obiettivo 70%'
										: 'nessun dato reale'
								}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard title="Immissione">
							<KpiBig
								value={hasPeriodData ? `${fmtNum(immissioneNetta)} kWh` : '-'}
								tone="accent"
								unit="netta in rete"
								trend={immissioneNetta > 0 ? 'surplus rete' : 'in attesa dati reali'}
								trendTone="up"
							/>
						</PanelCard>
						<PanelCard title="CO₂ Risparmiata">
							<KpiBig
								value={co2Periodo > 0 ? `${decIt(co2Periodo, 2)} t` : '-'}
								tone="purple"
								unit={`evitata ${periodNoun}`}
								trend={
									// Stesso periodo del valore: prima gli alberi erano dell'intero
									// storico accanto a «0,00 t» del periodo (INB-356).
									co2Periodo > 0
										? `≈ ${Math.max(1, Math.round(co2Periodo * 5))} alberi`
										: 'in attesa dati reali'
								}
								trendTone="up"
							/>
						</PanelCard>
					</Grid>
				)}
			</Stack>

			<Stack mb="lg">
				<PanelCard
					title={`${isDayPeriod ? 'Andamento Giornaliero Energia' : 'Andamento Mensile Energia'}${personal ? ' · configurazione' : ''}`}
				>
					{/* Sempre SlChart, mai lo StatusBlock testuale: senza dati nel
					    periodo, SlChart disegna già da solo una griglia sintetica con
					    il messaggio in overlay (stesso meccanismo generalizzato per
					    Monitoraggio/Previsione) — un'anteprima del grafico, non un
					    riquadro vuoto al suo posto. */}
					<SlChart
						series={[
							{
								name: 'Produzione',
								color: 'var(--color-series-2)',
								kind: 'area',
								vals: energyChart.prod,
							},
							{ name: 'Prelievo', color: 'var(--color-series-1)', vals: energyChart.prel },
						]}
						x={energyChart.labels}
						hoverX={(i) => energyChart.hoverLabels[i] ?? ''}
						unit="kWh"
						height={320}
						ariaLabel="Produzione e prelievo nel periodo selezionato"
						emptyMessage="Andamento disponibile dopo l'integrazione con i misuratori GSE"
					/>
				</PanelCard>
			</Stack>

			<Stack mb="lg">
				<PanelCard title="Traguardi">
					<MilestoneItem
						icon="🌍"
						iconTone="success"
						title="1 Tonnellata CO₂"
						sub={co2Totale > 0 ? `${decIt(co2Totale, 1)} t evitata` : 'in attesa dati'}
						tag={co2Totale >= 1 ? '✓' : `${Math.min(100, Math.round(co2Totale * 100))}%`}
						tagTone={co2Totale >= 1 ? 'green' : 'orange'}
					/>
					<MilestoneItem
						icon="🔗"
						iconTone="accent"
						title="100 MWh Condivisi"
						sub={hasAnyData ? `${fmtNum(Math.round(totalSharedKwh))} kWh` : 'Record collettivo'}
						tag={milestone100Mwh >= 100 ? '✓' : `${milestone100Mwh}%`}
						tagTone={milestone100Mwh >= 100 ? 'green' : 'orange'}
					/>
					<MilestoneItem
						icon="👥"
						iconTone="success"
						title={sociTarget > 0 ? `${sociTarget} Soci Target` : 'Soci Target'}
						sub={`${activeMembersCount} soci registrati`}
						tag={milestoneSoci >= 100 ? '✓' : `${milestoneSoci}%`}
						tagTone={milestoneSoci >= 100 ? 'green' : 'orange'}
					/>
					<MilestoneItem
						icon="🌐"
						iconTone="purple"
						title="Socio Ambassador"
						sub="Porta 3 nuovi soci"
						tag="Sblocca!"
						tagTone="purple"
					/>
				</PanelCard>
			</Stack>

			<Stack mb="lg">
				<PanelCard title="Classifica Community - Top Soci">
					{activeMembersCount === 0 ? (
						<StatusBlock
							kind="empty"
							size="sm"
							message="Nessun socio registrato - aggiungi soci nella sezione Soci"
						/>
					) : !hasAnyData ? (
						<StatusBlock
							kind="empty"
							size="sm"
							message="Dati energetici disponibili dopo l'integrazione con i misuratori GSE"
						/>
					) : (
						<DataTable columns={leaderboardColumns} rows={rankedMembers} rowKey={(m) => m.id} />
					)}
					{activeMembersCount > 6 && (
						<Footnote>+{activeMembersCount - 6} altri soci - visualizzati i primi 6</Footnote>
					)}
				</PanelCard>
			</Stack>

			<PanelCard title="Amministrazione">
				<Stack gap="sm">
					<Row align="center" justify="between">
						<Text
							variant="label"
							tone="muted"
							className="text-[11px] font-extrabold tracking-[.6px]"
						>
							Regole della comunità
						</Text>
						{isAdmin && !editingRules ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => {
									setRulesForm(rules);
									setEditingRules(true);
								}}
							>
								Modifica
							</Button>
						) : editingRules ? (
							<Row gap="sm">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setEditingRules(false)}
								>
									Annulla
								</Button>
								<Button type="button" variant="accent" size="sm" onClick={saveRules}>
									Salva
								</Button>
							</Row>
						) : undefined}
					</Row>
					{editingRules ? (
						<Grid columns={2} gap="md">
							{ruleFields.map(([key, label, placeholder]) => (
								<DetailField key={key} label={label}>
									<DetailTextarea
										value={rulesForm[key]}
										onChange={(v) => setRulesForm((f) => ({ ...f, [key]: v }))}
										placeholder={placeholder}
										rows={3}
									/>
								</DetailField>
							))}
						</Grid>
					) : (
						<Grid columns={2} gap="md">
							{ruleFields.map(([key, label]) => (
								<LabeledBox key={key} label={label.toUpperCase()}>
									{rules[key] ? (
										<Text pre>{rules[key]}</Text>
									) : (
										<Text italic tone="muted">
											{isAdmin
												? 'Clicca Modifica per aggiungere le regole.'
												: 'Nessuna regola definita.'}
										</Text>
									)}
								</LabeledBox>
							))}
						</Grid>
					)}
					{!isAdmin && (
						<Footnote>
							Le regole sono visibili a tutti i soci. Contatta il referente della configurazione per
							modifiche.
						</Footnote>
					)}
				</Stack>
			</PanelCard>
		</div>
	);
}
MySlimsView.displayName = 'MySlimsView';
